import { type EquipmentRow } from '@hvac/contracts';
import { Alert, Card, CardBody, cn, EmptyState, PageHeader, Pill, Skeleton } from '@hvac/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { equipmentQuery } from '../../shared/api/parties-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';

/**
 * Equipment.
 *
 * The machines the business has installed, each with its two warranty clocks
 *, parts and labour expire on different dates and customers ask about them
 * separately. The colouring answers the phone call: red is expired, amber is
 * inside ninety days, quiet is covered.
 */

export function warrantyTone(expiresOn: string | null, today: Date): string {
  if (expiresOn === null) return 'text-content-subtle';
  const expiry = new Date(`${expiresOn}T00:00:00Z`);
  const days = Math.floor((expiry.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return 'text-danger';
  if (days <= 90) return 'text-warning';
  return 'text-content-muted';
}

export function EquipmentPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const equipment = useQuery(equipmentQuery(organizationId));
  const today = new Date();

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        eyebrow="Business"
        title="Equipment"
        description="Every machine installed, with both warranty clocks, parts and labour expire separately, and customers ask about them separately."
      />

      {equipment.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : equipment.isError ? (
        <Alert tone="danger" title="Equipment could not be loaded">
          {equipment.error.message}
        </Alert>
      ) : equipment.data.length === 0 ? (
        <EmptyState
          icon="equipment"
          title="No equipment on record yet"
          body="Units appear as invoices mention serial numbers and as install jobs complete, each with both warranty clocks running."
        />
      ) : (
        <Card>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="sticky top-0 z-10 bg-surface text-left text-xs uppercase tracking-wider text-content-subtle shadow-[inset_0_-1px_0_var(--color-border)]">
                    <th className="px-5 py-3 font-semibold">Unit</th>
                    <th className="px-5 py-3 font-semibold">Where</th>
                    <th className="px-5 py-3 font-semibold">Installed</th>
                    <th className="px-5 py-3 font-semibold">Parts warranty</th>
                    <th className="px-5 py-3 font-semibold">Labour warranty</th>
                  </tr>
                </thead>
                <tbody>
                  {equipment.data.map((unit: EquipmentRow) => (
                    <tr
                      key={unit.id}
                      className="border-b border-border transition-colors duration-[var(--duration-quick)] odd:bg-surface-muted/40 last:border-0 hover:bg-brand-50/60"
                    >
                      <td className="px-5 py-3">
                        <Link
                          to="/equipment/$equipmentId"
                          params={{ equipmentId: unit.id }}
                          className="font-medium text-content hover:text-brand-700"
                        >
                          {unit.label}
                        </Link>
                        {unit.serialNumber === null ? null : (
                          <span className="ml-2 text-xs text-content-subtle">
                            {unit.serialNumber}
                          </span>
                        )}
                        {unit.status === 'active' ? null : (
                          <Pill tone="neutral" className="ml-2">
                            {unit.status}
                          </Pill>
                        )}
                      </td>
                      <td className="px-5 py-3 text-content-muted">
                        {unit.customerName ?? unit.propertyAddress ?? '-'}
                      </td>
                      <td className="px-5 py-3 text-content-muted">{unit.installedOn ?? '-'}</td>
                      <td
                        className={cn(
                          'px-5 py-3',
                          warrantyTone(unit.partsWarrantyExpiresOn, today),
                        )}
                      >
                        {unit.partsWarrantyExpiresOn ?? 'unknown'}
                      </td>
                      <td
                        className={cn(
                          'px-5 py-3',
                          warrantyTone(unit.labourWarrantyExpiresOn, today),
                        )}
                      >
                        {unit.labourWarrantyExpiresOn ?? 'unknown'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}
